import React, { useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
// import 'video.js/dist/video.js';
import { useTranslation } from 'react-i18next';
import 'videojs-contrib-quality-levels';
import 'videojs-hls-quality-selector';
import dayjs from 'dayjs';
import useThumbnailUrl from '../../hooks/useThumbnailUrl';
import { useRecoilValue } from 'recoil';
import { languageState } from '../../recoil/language/languageAtom';
import useApi from '../../hooks/useApi';
import { useNavigate } from 'react-router-dom';
import useGetLocationData from '../../hooks/useGetLocationData';
import LocalScreenLoadingSpinner from '../Common/LocalScreenLoadingSpinner';

const VideoPlayer = ({
  options,
  onReady,
  videoType,
  clipInfo,
  policy,
  signature,
  keyPairId,
  liveClipInfo,
  id,
  countryData,
}) => {
  // 해당 DOM 참고를 위한 ref
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const { t } = useTranslation();
  const [isLivePlaying, setIsLivePlaying] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  // cookieList refresh 코드 추가
  const selectedLanguage = useRecoilValue(languageState);
  const apiV1 = useApi('v1');
  const accessToken = localStorage.getItem('access_token');
  const navigate = useNavigate();

  // 업데이트용 cookieList useRef(업데이트 이전에는 최초 props)
  const cookieListRef = useRef({
    policy: policy,
    signature: signature,
    keyPairId: keyPairId,
  });

  let locationData = {
    location: 'seoul', // local 환경에서 기본값
  };

  // 배포 환경에서만 실행(local 환경에서 cors error)
  if (process.env.NODE_ENV === 'production') {
    locationData = useGetLocationData('https://location.mubeat.tv/');
  }
  // END cookieList refresh 코드 추가

  // 썸네일 이미지 객체 재가공
  const thumbnail_info = {
    scheme: clipInfo?.scheme,
    host: clipInfo?.host,
    imgpath: clipInfo?.imgpath,
  };

  let imageUrl;

  if (thumbnail_info) {
    imageUrl = useThumbnailUrl(thumbnail_info, 100);
  }

  // option.uri에 cookieList 삽입(policy, signature, keyPairId)
  // TODO: http-streaming 3버전 오면서 beforeRequest에서 onRequest로 변경
  // https://www.npmjs.com/package/@videojs/http-streaming/v/2.16.3
  videojs.Vhs.xhr.beforeRequest = function (options) {
    // 기존값에 query string이 있는 경우 원본 적용
    if (options.uri.indexOf('?') >= 0) {
      return options;
    }

    // 하단에서 cloudFront 403 error 나왔을 때 신규 업데이트된 useRef로 signedURL 처리
    const secureUri = `${options.uri}?Policy=${cookieListRef.current.policy}&Signature=${cookieListRef.current.signature}&Key-Pair-Id=${cookieListRef.current.keyPairId}`;
    options.uri = options.uri.replace(options.uri, secureUri);

    return options;
  };

  let liveMessage;

  // 종료: end, 송출중: streaming, 준비: ready, 임시중지: paused
  switch (clipInfo?.live_status) {
    case 'ready':
      liveMessage = t('live_soon_msg');
      break;
    case 'end':
      liveMessage = t('live_end_msg');
      break;
    default:
      liveMessage = '';
  }

  // cookieList 업데이트
  const updateCookieList = async () => {
    const newCookieList = await fetchNewCookieList();
    if (newCookieList) cookieListRef.current = newCookieList;
  };

  // 신규 cookieList 가져오기
  const fetchNewCookieList = async () => {
    const lang = selectedLanguage === 'ko' ? 'KO' : 'EN';

    if (countryData && countryData.country) {
      try {
        const response = await apiV1.get(
          `clip/play/${id}?country=${countryData?.country}&location=${locationData?.location}&lang=${lang}`,
          {
            headers: {
              Authorization: accessToken,
            },
          },
        );

        const {
          ['CloudFront-Key-Pair-Id']: keyPairId,
          ['CloudFront-Policy']: policy,
          ['CloudFront-Signature']: signature,
        } = response.data.cookieList;

        cookieListRef.current.keyPairId = keyPairId;
        cookieListRef.current.policy = policy;
        cookieListRef.current.signature = signature;

        return cookieListRef.current;
      } catch (error) {
        if (error.code === 'ERR_NETWORK' || error.code === 'ERR_BAD_RESPONSE') {
          navigate('/error');
        }
      }
    }
  };

  // 플레이어 인스턴스 생성 함수
  const initializePlayer = () => {
    // player 인스턴스가 라이프사이클에서 한번 초기화 필요함
    if (!playerRef.current) {
      const videoElement = document.createElement('video-js');
      // 필요한 video.js 관련 class 추가
      videoElement.classList.add('vjs-big-play-centered');
      videoRef.current.appendChild(videoElement);

      const player = (playerRef.current = videojs(videoElement, options, () => {
        onReady && onReady(player);
      }));

      // 플레이어 시작 시 실행
      player.on('play', () => {
        setIsLivePlaying(true);
        // 라이브 클립일 경우
        if (videoType === 'live') player.liveTracker.seekToLiveEdge(); // 항상 라이브엣지(최신)으로 이동
      });

      // 커스텀 에러 오버레이
      player.on('error', () => {
        let errorDisplay = player.getChild('errorDisplay');
        errorDisplay.contentEl().innerHTML =
          '<div class="custom-error-wrapper"><p class="custom-error-message">The media could not be loaded, either because the server or network failed or because the format is not supported.</p><button type="button" class="retry-button">재시도</button></div>';

        // 재시도 버튼에 클릭 이벤트 리스너 추가
        document.querySelector('.retry-button').addEventListener('click', () => {
          handleRetry();
        });
      });

      player.qualityLevels();
      player.hlsQualitySelector({
        displayCurrentQuality: true,
      });
    } else {
      const player = playerRef.current;

      player.autoplay(options.autoplay);
      player.src(options.sources);
    }
  };

  const handleRetry = () => {
    setIsRetrying(true);

    updateCookieList();
    initializePlayer();

    setTimeout(() => {
      setIsRetrying(false);
    }, 3000);
  };

  useEffect(() => {
    // liveclip의 경우 live_date가 'streaming' 이 아닐 때 video.js 인스턴스 생성 X, 재생 버튼 X
    if (clipInfo && clipInfo?.live_status && clipInfo?.live_status !== 'streaming') {
      videoRef.current.classList.add('type-hide-vjs-big-play');
      return;
    }

    // player 인스턴스 생성
    // player 인스턴스가 라이프사이클에서 한번 초기화 필요함
    if (!playerRef.current) {
      const videoElement = document.createElement('video-js');
      // 필요한 video.js 관련 class 추가
      videoElement.classList.add('vjs-big-play-centered');
      videoRef.current.appendChild(videoElement);

      const player = (playerRef.current = videojs(videoElement, options, () => {
        onReady && onReady(player);
      }));

      // video.js 내부 호출에서 ts 파일 403 에러 핸들링 목적으로 추가
      // interval로 cookie 갱신으로 코드 주석 처리, 별도 에러 핸들링 활용 가능성이 있어 코드는 유지
      // player.on('ready', () => {
      //   const tech = player.tech({
      //     IWillNotUseThisInPlugins: true, // 내부 api 직접 접근 허용 옵션(hls 내부 핸들러 감지 위해 허용)
      //   });
      //
      //   // vhs-rendition-blacklisted, hls-rendition-blacklisted 발생 시 cookieList 업데이트
      //   tech.on('usage', async (event) => {
      //     if (event.name === 'vhs-rendition-blacklisted' || event.name === 'hls-rendition-blacklisted') {
      //       const currentTime = player.currentTime();
      //       player.currentTime(currentTime); // safari 대응
      //       player.pause();
      //       player
      //         .play()
      //         .then(() => {
      //           try {
      //             updateCookieList();
      //             player.src(options.sources);
      //             player.currentTime(currentTime);
      //             player
      //               .play()
      //               .then(() => {
      //                 player.currentTime(currentTime);
      //               })
      //               .catch((error) => {
      //                 console.log(error);
      //               });
      //           } catch (error) {
      //             console.log(error);
      //           }
      //         })
      //         .catch((error) => {
      //           console.log(error);
      //         });
      //     }
      //   });
      // });
      // END video.js 내부 호출에서 ts 파일 403 에러 핸들링 목적으로 추가

      // 플레이어 시작 시 실행
      player.on('play', () => {
        setIsLivePlaying(true);
        // 라이브 클립일 경우
        if (videoType === 'live') player.liveTracker.seekToLiveEdge(); // 항상 라이브엣지(최신)으로 이동
      });

      player.on('error', () => {
        let errorDisplay = player.getChild('errorDisplay');
        // 에러 문구는 추후에 필요 시 추가
        errorDisplay.contentEl().innerHTML =
          '<div class="custom-error-wrapper"><p class="custom-error-message"></p><button type="button" class="button-default retry-button"><i class="icon icon-retry"></i></button></div>';

        // 재시도 버튼에 클릭 이벤트 리스너 추가
        document.querySelector('.retry-button').addEventListener('click', () => {
          handleRetry();
        });
      });

      player.qualityLevels();
      player.hlsQualitySelector({
        displayCurrentQuality: true,
      });
    } else {
      const player = playerRef.current;

      player.autoplay(options.autoplay);
      player.src(options.sources);
    }
  }, [options, videoRef]);

  // 해당 컴포넌트 unmount 시에 플레이어 삭제
  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  // 컴포넌트 진입 시 cookieList 업데이트를 위한 사이드이펙트 추가
  useEffect(() => {
    // cookieList expire 전에 cookieList 업데이트
    const updateBeforeExpire = () => {
      updateCookieList();
    };

    let intervalTime;

    // videoType 및 환경에 따라 인터벌 시간 설정
    // aws cloudFront cookie expire 정보
    // dev - 일반클립 1분, 라이브클립 3시간
    // prod - 일반클립 2시간, 라이브클립 3시간
    // 일반 클립의 경우
    if (videoType === 'replay') {
      if (process.env.REACT_APP_SETTINGS_MODE === 'local' || process.env.REACT_APP_SETTINGS_MODE === 'dev') {
        intervalTime = 50 * 1000; // 개발 환경에서 50초
      } else if (process.env.REACT_APP_SETTINGS_MODE === 'prod') {
        intervalTime = (1 * 60 * 60 + 50 * 60) * 1000; // prod 환경에선 1시간 50분마다 갱신
      }
      // 라이브클립의 경우
    } else if (videoType === 'live') {
      intervalTime = (2 * 60 * 60 + 50 * 60) * 1000; // 2시간 50분마다 갱신(dev, prod 동일)
    }

    if (intervalTime) {
      const intervalId = setInterval(() => {
        updateBeforeExpire();
      }, intervalTime);

      return () => clearInterval(intervalId);
    }
  }, []);

  return (
    <div data-vjs-player className={'ml-video-player'}>
      {/* 재시도중일 때: 짧은 시간 안에 너무 여러 번 호출되지 않도록 로컬 로딩스피너 노출 */}
      {isRetrying ? <LocalScreenLoadingSpinner /> : null}
      {/* 송출중일 때: 재생 버튼 누르기 전까지 썸네일 노출 */}
      {videoType === 'live' && clipInfo?.live_status === 'streaming' && !isLivePlaying ? (
        <figure className={'player-thumbnail type-streaming-thumbnail'}>
          <img
            src={imageUrl || '/images/img_no_img.png'}
            alt='player-thumbnail-image'
            className={'player-thumbnail-img'}
            onError={(e) => {
              e.target.onerror = null; // onError 이미지도 실패했을 때 무한루프 방지
              e.target.src = '/images/img_no_img.png';
            }}
            decoding={'async'}
          />
        </figure>
      ) : null}

      {/* 송출 전일 때: 송출 전 live_status에 따라 분기 처리 */}
      {/* 종료: end, 송출중: streaming, 준비: ready, 임시중지: paused */}
      {videoType === 'live' && clipInfo?.live_status !== 'streaming' ? (
        <>
          <figure className={'player-thumbnail'}>
            <img
              src={imageUrl || '/images/img_no_img.png'}
              alt='player-thumbnail-image'
              className={'player-thumbnail-img'}
              onError={(e) => {
                e.target.onerror = null; // onError 이미지도 실패했을 때 무한루프 방지
                e.target.src = '/images/img_no_img.png';
              }}
              decoding={'async'}
            />
          </figure>
          <div className='player-dim'>
            <div className='dim-text-group'>
              <p className={'dim-message'}>{liveMessage}</p>
              {/* 종료: end, 송출중: streaming, 준비: ready, 임시중지: paused */}
              {clipInfo?.live_status === 'ready' && liveClipInfo ? (
                <p className={'dim-date'}>{dayjs(liveClipInfo?.live_date).format('YYYY.MM.DD hh:mm')} (KST)</p>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
      <div ref={videoRef} />
    </div>
  );
};

export default VideoPlayer;
